import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, TariffFiling, TariffFilingStatus, TariffRateLine, UtilityKind, UtilityOperator } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['ENERGY_OFFICER'];
const UTILITY_KINDS: UtilityKind[] = ['ELECTRICITY', 'WATER', 'FUEL', 'OTHER'];
const STATUSES: TariffFilingStatus[] = ['FILED', 'UNDER_REVIEW', 'PUBLIC_COMMENT', 'APPROVED', 'REJECTED', 'WITHDRAWN'];

/** Utility tariff schedule filings — regulator review + public comment period, published via OpenGov Gazette on approval (M35). */
export function TariffFilings() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENERGY_OFFICER') ?? false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/m35/tariff-filings?${params.toString()}`;
  }, [status]);
  const listQ = useApiQuery<Page<TariffFiling>>(path);

  return (
    <div>
      <PageHeader
        title="Tariff Filings"
        subtitle="Proposed electricity/water/fuel tariff schedules — filing fee -> review -> public comment -> approve/reject, gazetted via OpenGov (M35)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + File tariff schedule
          </button>
        }
      />

      <div className="mb-4">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No tariff filings match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Utility</th>
                <th className="table-head">Category</th>
                <th className="table-head">Requested effective</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((t) => (
                <tr key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(t.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{t.refNo}</td>
                  <td className="table-cell">
                    <StatusBadge status={t.utilityType} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{t.tariffCategory}</td>
                  <td className="table-cell text-xs">{formatDate(t.effectiveDateRequested)}</td>
                  <td className="table-cell">
                    <StatusBadge status={t.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <DetailModal id={selectedId} isOfficer={isOfficer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DetailModal({ id, isOfficer, onClose, onChanged }: { id: string; isOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<TariffFiling>(`/api/v1/m35/tariff-filings/${id}`);
  const t = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t ? t.refNo : 'Tariff filing'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {t ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={t.status} />} />
            <Detail label="Utility" value={<StatusBadge status={t.utilityType} />} />
            <Detail label="Category" value={t.tariffCategory} />
            <Detail label="Fee" value={`${scr(t.feeAmountScr)} (${t.feeStatus})`} />
            <Detail label="Requested effective date" value={formatDate(t.effectiveDateRequested)} />
            {t.publicCommentClosesAt ? <Detail label="Comment closes" value={formatDateTime(t.publicCommentClosesAt)} /> : null}
          </div>
          <div>
            <div className="field-label">Proposed rates</div>
            <ul className="text-xs text-slate-600">
              {t.proposedRates.map((r, i) => (
                <li key={i}>
                  {r.band}: {scr(r.unitPriceScr)}
                </li>
              ))}
            </ul>
          </div>
          {t.justification ? <p className="text-xs text-slate-500">{t.justification}</p> : null}
          {t.rejectionReason ? <ErrorNote message={`Rejected: ${t.rejectionReason}`} /> : null}
          {t.feeStatus !== 'PAID' ? <p className="text-xs text-amber-600">Filing fee confirmation arrives via the SeyPay webhook (service-token only).</p> : null}

          {isOfficer && t.status === 'FILED' ? (
            <button className="btn-primary" disabled={busy || t.feeStatus !== 'PAID'} onClick={() => act(() => api.post(`/api/v1/m35/tariff-filings/${id}/claim-review`))}>
              Begin review
            </button>
          ) : null}

          {isOfficer && t.status === 'UNDER_REVIEW' ? (
            <div className="flex flex-wrap gap-2">
              <button className="btn-secondary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/tariff-filings/${id}/open-comment`, { closesInDays: 21 }))}>
                Open public comment (21 days)
              </button>
              <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/tariff-filings/${id}/approve`))}>
                Approve directly
              </button>
              <div className="flex gap-2">
                <input className="field-input" placeholder="Rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <button className="btn-danger" disabled={busy || rejectReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m35/tariff-filings/${id}/reject`, { reason: rejectReason }))}>
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          {isOfficer && t.status === 'PUBLIC_COMMENT' ? (
            <div className="flex flex-wrap gap-2">
              <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m35/tariff-filings/${id}/approve`))}>
                Approve (publishes Gazette notice)
              </button>
              <div className="flex gap-2">
                <input className="field-input" placeholder="Rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <button className="btn-danger" disabled={busy || rejectReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m35/tariff-filings/${id}/reject`, { reason: rejectReason }))}>
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          {t.openGovNoticeRef ? <p className="text-xs text-slate-400">OpenGov notice {t.openGovNoticeRef}</p> : null}
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const operatorsQ = useApiQuery<Page<UtilityOperator>>('/api/v1/m35/operators?limit=100');
  const [operatorId, setOperatorId] = useState('');
  const [utilityType, setUtilityType] = useState<UtilityKind>('ELECTRICITY');
  const [tariffCategory, setTariffCategory] = useState('');
  const [rates, setRates] = useState<TariffRateLine[]>([{ band: '', unitPriceScr: 0 }]);
  const [effectiveDateRequested, setEffectiveDateRequested] = useState(() => new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10));
  const [justification, setJustification] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m35/tariff-filings', {
        operatorId,
        utilityType,
        tariffCategory,
        proposedRates: rates.filter((r) => r.band.trim().length > 0),
        effectiveDateRequested,
        justification: justification || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to file tariff schedule');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="File proposed tariff schedule" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Operator">
          <select className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)}>
            <option value="">Select an operator…</option>
            {operatorsQ.data?.items.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Utility type">
            <select className="field-input" value={utilityType} onChange={(e) => setUtilityType(e.target.value as UtilityKind)}>
              {UTILITY_KINDS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tariff category">
            <input className="field-input" value={tariffCategory} onChange={(e) => setTariffCategory(e.target.value)} placeholder="e.g. Domestic — 0-200 units/month" />
          </Field>
        </div>
        <Field label="Proposed rate bands">
          <div className="space-y-1.5">
            {rates.map((r, i) => (
              <div key={i} className="flex gap-2">
                <input
                  className="field-input"
                  placeholder="Band, e.g. 0-200 kWh"
                  value={r.band}
                  onChange={(e) => setRates((prev) => prev.map((p, idx) => (idx === i ? { ...p, band: e.target.value } : p)))}
                />
                <input
                  className="field-input !w-40"
                  type="number"
                  placeholder="Unit price SCR"
                  value={r.unitPriceScr || ''}
                  onChange={(e) => setRates((prev) => prev.map((p, idx) => (idx === i ? { ...p, unitPriceScr: Number.parseFloat(e.target.value) || 0 } : p)))}
                />
                <button className="btn-secondary !px-2" type="button" onClick={() => setRates((prev) => prev.filter((_, idx) => idx !== i))}>
                  −
                </button>
              </div>
            ))}
            <button className="btn-secondary" type="button" onClick={() => setRates((prev) => [...prev, { band: '', unitPriceScr: 0 }])}>
              + Add rate band
            </button>
          </div>
        </Field>
        <Field label="Requested effective date">
          <input className="field-input" type="date" value={effectiveDateRequested} onChange={(e) => setEffectiveDateRequested(e.target.value)} />
        </Field>
        <Field label="Justification (optional)">
          <textarea className="field-input" rows={2} value={justification} onChange={(e) => setJustification(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !operatorId || !tariffCategory || rates.every((r) => !r.band.trim())} onClick={submit}>
            {busy ? 'Filing…' : 'File tariff schedule'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
